import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { BiChevronDown, BiChevronRight, BiSearch, BiUpload, BiX } from "react-icons/bi";
import axios from "axios";

import PageHeader from "../../components/ui/PageHeader";

/**
 * Kategoriyalar.
 *
 * Ro'yxat 1C dan keladi. 1C almashinuvda tovar bilan birga faqat **bitta
 * satr** yuboradi ("14-10 Lenta chiroqlar") — papkalar ierarxiyasini emas.
 * Shuning uchun ierarxiya alohida yuklanadi: 1C ning "Ведомость по товарам
 * на складах" hisoboti papkalarni ichma-ich chiqaradi va shu fayl tepadagi
 * tugma orqali yuklanadi.
 *
 * Bu sahifadan boshqariladigan narsa — kategoriya botda va ilovada
 * ko'rinadimi, qanday nom bilan, qaysi tartibda va qaysi papka ichida.
 * 1C dagi nomga tegilmaydi: u har sinxronizatsiyada qaytadan yoziladi.
 *
 * "Tovar" ustunida ikkita son bo'lishi mumkin: birinchisi — shu papkaning
 * o'zida yotgan tovarlar, qavs ichidagisi — ichidagilari bilan qo'shib.
 * Oraliq papkalarda tovar yotmaydi, ya'ni ularda birinchisi doim nol.
 *
 * Tezlik haqida. Jadvalda 130 ta qator bor va har biri MUI'ning bir necha
 * boshqaruv elementidan iborat. Birinchi variantda har qatorda alohida
 * Autocomplete turardi va sahifadagi har qanday o'zgarish (qidiruvga bitta
 * harf yozish ham) 130 ta qatorni to'liq qayta chizardi — o'lchanganda
 * 0,2–1 s. Shuning uchun: qator `memo` bilan o'ralgan va faqat o'z
 * ma'lumoti o'zgarganda chiziladi; ota papka tanlash bitta umumiy oynada,
 * qatorda esa faqat nomi; qidiruv `useDeferredValue` bilan — harf darrov
 * chiqadi, jadval keyin yangilanadi.
 */

const API = process.env.REACT_APP_API;
const auth = () => ({
  headers: { Authorization: localStorage.getItem("token") },
  // Cheklovsiz so'rov osilib qolsa sahifa abadiy "yuklanmoqda" turardi.
  timeout: 60000,
});

/** Daraxtni ro'yxatga yoyadi, har qatorga chuqurligini qo'shib. */
const flatten = (nodes, depth = 0, out = [], parentHidden = false) => {
  const sorted = [...nodes].sort(
    (a, b) =>
      (a.sortOrder ?? 100) - (b.sortOrder ?? 100) ||
      (a.name || "").localeCompare(b.name || "")
  );
  for (const node of sorted) {
    const kids = node.children || [];
    // parentHidden — ota-bobolaridan biri yashirilgan: bot va ilovada bunday
    // qator o'zi yashirilmagan bo'lsa ham ko'rinmaydi.
    out.push({ ...node, depth, hasChildren: kids.length > 0, parentHidden });
    flatten(kids, depth + 1, out, parentHidden || Boolean(node.hidden));
  }
  return out;
};

/** Ochiq papkalar brauzerda eslab qolinadi — har kirganda qayta ochib o'tirmaslik uchun. */
const EXPANDED_KEY = "alight-crm.categories.expanded";
const readExpanded = () => {
  try {
    const v = JSON.parse(localStorage.getItem(EXPANDED_KEY) || "[]");
    return new Set(Array.isArray(v) ? v : []);
  } catch {
    return new Set();
  }
};
const writeExpanded = (set) => {
  try {
    localStorage.setItem(EXPANDED_KEY, JSON.stringify([...set]));
  } catch {
    // xotira yopiq bo'lsa shunchaki eslab qolinmaydi
  }
};

/** Kategoriyaning o'zi va uning ichidagilar — ota tanlashda taqiqlanadi. */
const subtreeIds = (rows, id) => {
  const banned = new Set([id]);
  // Ro'yxat ota-bola tartibida yoyilgan, ya'ni bitta o'tish yetarli.
  for (const row of rows) {
    if (row.parentId && banned.has(row.parentId)) banned.add(row.id);
  }
  return banned;
};

const mono = { fontFamily: "'JetBrains Mono', monospace" };

/**
 * Qator o'qiydigan maydonlar. `memo` shular bo'yicha solishtiradi, obyekt
 * o'ziga qarab emas: saqlashdan keyin daraxt qayta quriladi va har qator
 * yangi obyekt bo'ladi -- oddiy taqqoslashda 130 qatorning hammasi qayta
 * chizilardi (o'lchandi: 141 ta render, ~160 ms; maydon bo'yicha -- 2 ta).
 */
const ROW_FIELDS = [
  "id",
  "name",
  "displayName",
  "hidden",
  "parentHidden",
  "hasChildren",
  "sortOrder",
  "productCount",
  "totalProductCount",
  "depth",
];
const sameRow = (a, b) =>
  a.saving === b.saving &&
  a.open === b.open &&
  a.parentName === b.parentName &&
  a.onPatch === b.onPatch &&
  a.onPickParent === b.onPickParent &&
  a.onToggle === b.onToggle &&
  ROW_FIELDS.every((k) => a.c[k] === b.c[k]);

/**
 * Bitta qator.
 *
 * `memo`: sahifa qayta chizilganda faqat props'i o'zgargan qatorlar
 * chiziladi. Shuning uchun bu yerga faqat oddiy qiymatlar va barqaror
 * funksiyalar beriladi — `saving` mantiqiy qiymat, `savingId` emas, aks
 * holda bittasi saqlanayotganda hammasi chizilardi.
 */
const CategoryRow = memo(function CategoryRow({
  c,
  parentName,
  saving,
  open,
  onToggle,
  onPatch,
  onPickParent,
}) {
  const nested = (c.totalProductCount ?? 0) > (c.productCount ?? 0);
  const shortName = c.displayName === c.name ? "" : c.displayName || "";
  const dimmed = c.hidden || c.parentHidden;

  return (
    <TableRow hover sx={{ opacity: dimmed ? 0.55 : 1 }}>
      <TableCell>
        {/* Chekinish daraxtdagi o'rnini ko'rsatadi. Qidirilganda ham
            qoldiriladi: qator qayerdan kelganini bildiradi. */}
        <Box sx={{ display: "flex", alignItems: "center", pl: `${c.depth * 22}px` }}>
          {c.hasChildren ? (
            <IconButton size="small" onClick={() => onToggle(c.id)} sx={{ mr: 0.5, p: 0.25 }}>
              {open ? <BiChevronDown /> : <BiChevronRight />}
            </IconButton>
          ) : (
            // Bargda tugma o'rnida bo'sh joy — nomlar bir chiziqda tursin.
            <Box sx={{ width: 28, mr: 0.5, textAlign: "center", color: "text.disabled" }}>
              {c.depth > 0 ? "└" : ""}
            </Box>
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: c.hasChildren ? 600 : 400,
              color: c.hasChildren ? "text.primary" : "text.secondary",
            }}
          >
            {c.name || <em>nomsiz</em>}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <TextField
          placeholder={c.name}
          defaultValue={shortName}
          variant="standard"
          fullWidth
          InputProps={{ disableUnderline: true, sx: { fontSize: "0.875rem" } }}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== shortName) onPatch(c.id, { displayName: v });
          }}
        />
      </TableCell>
      <TableCell>
        {/*
          Har qatorda Autocomplete emas, oddiy tugma: 130 ta Autocomplete
          sahifaning eng og'ir qismi edi. Tanlash oynasi bitta, sahifada.
        */}
        <Button
          size="small"
          variant="text"
          disabled={saving}
          onClick={() => onPickParent(c.id)}
          sx={{
            textTransform: "none",
            justifyContent: "flex-start",
            fontWeight: 400,
            fontSize: "0.8125rem",
            color: parentName ? "text.primary" : "text.disabled",
            px: 1,
            minWidth: 0,
            maxWidth: 220,
          }}
        >
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {parentName || "— ildizda —"}
          </Box>
        </Button>
      </TableCell>
      <TableCell align="center">
        <Typography
          variant="body2"
          sx={{ ...mono, color: c.productCount ? "text.primary" : "text.disabled" }}
        >
          {c.productCount ?? 0}
          {nested && (
            <Box component="span" sx={{ color: "text.secondary", ml: 0.5 }}>
              ({c.totalProductCount})
            </Box>
          )}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <TextField
          type="number"
          defaultValue={c.sortOrder ?? 100}
          variant="standard"
          inputProps={{ step: 1, style: { textAlign: "center", ...mono } }}
          InputProps={{ disableUnderline: true, sx: { fontSize: "0.8125rem", width: 56 } }}
          onBlur={(e) => {
            const current = c.sortOrder ?? 100;
            const raw = e.target.value.trim();
            const v = Number(raw);
            // Bo'sh yoki butun bo'lmagan qiymat -- o'zgarish emas. Number("")
            // nolga teng va kategoriya sezdirmay eng tepaga chiqib ketardi;
            // kasr esa serverda 400 berardi.
            if (raw === "" || !Number.isInteger(v)) {
              e.target.value = String(current);
              return;
            }
            if (v !== current) onPatch(c.id, { sortOrder: v });
          }}
        />
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
          {c.hidden ? (
            <Chip size="small" label="Yashirin" variant="outlined" />
          ) : (
            c.parentHidden && (
              <Tooltip title="Papkasi yashirilgan, shuning uchun bu ham ko'rinmaydi">
                <Chip size="small" label="Papkasi yashirin" variant="outlined" />
              </Tooltip>
            )
          )}
          <Tooltip title={c.hidden ? "Ko'rsatish" : "Yashirish"}>
            <span>
              <Switch
                checked={!c.hidden}
                disabled={saving}
                onChange={(e) => onPatch(c.id, { hidden: !e.target.checked })}
              />
            </span>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}, sameRow);

const CategoriesPage = () => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState("");
  // Harf darrov chiqsin, 130 qatorli jadval esa bo'shagan paytda yangilansin.
  const deferredSearch = useDeferredValue(search);

  // Ochiq papkalar. Boshida hammasi yopiq: 130 qator o'rniga 12 ta bo'lim
  // ko'rinadi, kerakligi ochiladi.
  const [expanded, setExpanded] = useState(readExpanded);
  useEffect(() => {
    writeExpanded(expanded);
  }, [expanded]);

  // Ota papka tanlash oynasi: qaysi kategoriya uchun ochilgan.
  const [pickingId, setPickingId] = useState(null);

  // Fayl yuklash: avval sinov (dryRun), natijasi ko'rsatiladi, keyin tasdiq.
  const fileRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);

  // load() birinchi marta aylanma ko'rsatadi, keyingilarida jadval
  // joyida qoladi — aks holda har ko'chirishdan keyin 130 qator
  // yo'qolib qaytadan chizilardi, yozilayotgan matn va o'rin yo'qolardi.
  const hasData = useRef(false);

  /** @returns {Promise<boolean>} muvaffaqiyatli bo'ldimi */
  const load = useCallback(async () => {
    if (hasData.current) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await axios.get(`${API}/category/all/tree`, auth());
      setTree(Array.isArray(data) ? data : []);
      hasData.current = true;
      // "Qayta urinish" muvaffaqiyatli bo'lsa eski xato banneri ham ketsin.
      setError(null);
      return true;
    } catch {
      setError("Kategoriyalar ro'yxatini olib bo'lmadi.");
      return false;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => flatten(tree), [tree]);
  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const folderIds = useMemo(() => rows.filter((r) => r.hasChildren).map((r) => r.id), [rows]);
  const allOpen = folderIds.length > 0 && folderIds.every((id) => expanded.has(id));
  const toggleAll = () => setExpanded(allOpen ? new Set() : new Set(folderIds));

  const patch = useCallback(async (id, params) => {
    setSavingId(id);
    try {
      const { data } = await axios.patch(
        `${API}/category/${id}?${new URLSearchParams(params)}`,
        null,
        auth()
      );
      // Javob Category entity'si, daraxt yozuvi emas. Daraxtni qaytadan
      // qurmaslik uchun faqat o'zgargan maydonlar joyiga qo'yiladi.
      // Eski qatorlarda `sortOrder`/`hidden` bazada null — daraxt DTO'si
      // ularni 100/false qilib beradi, entity esa null'ligicha; shuning
      // uchun bu yerda ham xuddi shunday tekislanadi.
      const put = (nodes) =>
        nodes.map((n) =>
          n.id === id
            ? {
                ...n,
                hidden: Boolean(data.hidden),
                sortOrder: data.sortOrder ?? 100,
                displayName: data.displayName || data.name,
              }
            : { ...n, children: put(n.children || []) }
        );
      setTree(put);
      setError(null);
    } catch (err) {
      setError(
        err.response?.status === 403
          ? "Kategoriyani o'zgartirish uchun huquqingiz yo'q."
          : "Saqlab bo'lmadi."
      );
    } finally {
      setSavingId(null);
    }
  }, []);

  /** Kategoriyani boshqa papkaga ko'chiradi. Daraxt o'zgargani uchun qayta yuklanadi. */
  const move = useCallback(
    async (id, parentId) => {
      setSavingId(id);
      try {
        const q = parentId ? `?parentId=${parentId}` : "";
        await axios.patch(`${API}/category/update/parent/${id}${q}`, null, auth());
        // load() xatoni o'zi yozadi; muvaffaqiyatda esa eskisini tozalaymiz.
        if (await load()) setError(null);
      } catch (err) {
        setError(
          err.response?.status === 400
            ? "Kategoriyani o'zining ichiga solib bo'lmaydi."
            : "Ko'chirib bo'lmadi."
        );
      } finally {
        setSavingId(null);
      }
    },
    [load]
  );

  const openPicker = useCallback((id) => setPickingId(id), []);

  /** Faylni avval sinov rejimida yuboradi — nima o'zgarishini ko'rsatish uchun. */
  const chooseFile = async (file) => {
    if (!file) return;
    setImporting(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(
        `${API}/category/tree/import?dryRun=true`,
        form,
        auth()
      );
      setPending(file);
      setPreview(data);
      setImportError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Faylni o'qib bo'lmadi.");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", pending);
      await axios.post(`${API}/category/tree/import`, form, auth());
      setPreview(null);
      setPending(null);
      await load();
    } catch (err) {
      // Xato oynaning ichida ko'rsatiladi: sahifadagi Alert modal orqasida
      // xira ko'rinib, "hech narsa bo'lmadi" degan taassurot qoldirardi.
      setImportError(err.response?.data?.message || "Yuklab bo'lmadi.");
    } finally {
      setImporting(false);
    }
  };

  const closePreview = () => {
    if (importing) return;
    setPreview(null);
    setImportError(null);
  };

  const searching = deferredSearch.trim().length >= 2;
  const visible = useMemo(() => {
    if (searching) {
      // Qidiruvda yopiq papkalar hisobga olinmaydi — topilgani ko'rinsin.
      const q = deferredSearch.trim().toLowerCase();
      return rows.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.displayName || "").toLowerCase().includes(q)
      );
    }
    // Qator ko'rinadi, agar hamma ota-bobolari ochiq bo'lsa. Ro'yxat
    // ota-bola tartibida, shuning uchun bitta o'tish yetarli.
    const shownIds = new Set();
    const out = [];
    for (const c of rows) {
      if (!c.parentId || (shownIds.has(c.parentId) && expanded.has(c.parentId))) {
        shownIds.add(c.id);
        out.push(c);
      }
    }
    return out;
  }, [rows, deferredSearch, searching, expanded]);

  const shown = rows.filter((c) => !c.hidden && !c.parentHidden).length;
  const orphans = rows.filter((c) => c.depth === 0 && !(c.children || []).length).length;

  const picking = pickingId == null ? null : byId.get(pickingId) || null;
  const pickerOptions = useMemo(() => {
    if (!picking) return [];
    const banned = subtreeIds(rows, picking.id);
    return rows.filter((r) => !banned.has(r.id));
  }, [rows, picking]);

  return (
    <Box>
      <PageHeader
        title="Kategoriyalar"
        subtitle={`${shown} tasi botda va ilovada ko'rinadi, ${rows.length - shown} tasi yashirilgan.`}
        actions={
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              placeholder="Qidirish"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: "100%", sm: 240 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BiSearch />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="text" onClick={toggleAll} disabled={folderIds.length === 0}>
              {allOpen ? "Hammasini yopish" : "Hammasini ochish"}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              hidden
              onChange={(e) => chooseFile(e.target.files?.[0])}
            />
            <Button
              variant="outlined"
              startIcon={<BiUpload />}
              disabled={importing}
              onClick={() => fileRef.current?.click()}
            >
              1C daraxtini yuklash
            </Button>
          </Box>
        }
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          // MUI `action` berilganda o'zining yopish tugmasini chizmaydi,
          // shuning uchun ikkalasi ham shu yerda. "Qayta urinish" faqat
          // ro'yxat umuman kelmaganda -- saqlash xatosida u ma'nosiz.
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {!loading && rows.length === 0 && (
                <Button color="inherit" size="small" onClick={() => load()}>
                  Qayta urinish
                </Button>
              )}
              <IconButton size="small" color="inherit" aria-label="Yopish" onClick={() => setError(null)}>
                <BiX />
              </IconButton>
            </Box>
          }
        >
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Kategoriyalar 1C dan o'zi to'ladi, lekin <b>ichma-ich tuzilishi
        kelmaydi</b> — 1C tovar bilan birga faqat papkaning nomini yuboradi.
        Daraxtni tiklash uchun 1C dan "Ведомость по товарам на складах"
        hisobotini xlsx qilib saqlang va yuqoridagi tugma orqali yuklang.
        Alohida kategoriyani qo'lda ham ko'chirsa bo'ladi — "Ota papka"
        ustunidan. Papka yashirilsa ichidagilar ham ko'rinmaydi.
        {orphans > 0 &&
          ` Hozir ${orphans} ta kategoriya hech qaysi papkaga kirmaydi.`}
      </Alert>

      <Paper sx={{ overflow: "hidden" }}>
        {/* Qayta yuklashda jadval joyida qoladi, tepada ingichka chiziq yuradi. */}
        <Box sx={{ height: 3 }}>{refreshing && <LinearProgress sx={{ height: 3 }} />}</Box>
        {loading ? (
          <Box sx={{ p: 6, display: "grid", placeItems: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 && error ? null : rows.length === 0 ? (
          // Ro'yxat kelmagan bo'lsa (xato bor) "kategoriya yo'q" deyilmaydi --
          // tepadagi banner allaqachon nima bo'lganini aytib turibdi.
          <Box sx={{ p: 6, textAlign: "center" }}>
            <Typography variant="h4">Kategoriya yo'q</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Kategoriyalar 1C dan tovarlar bilan birga keladi. Bu har soatda avtomatik bajariladi.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>1C dagi nomi</TableCell>
                  <TableCell>Ko'rsatiladigan nom</TableCell>
                  <TableCell width={220}>Ota papka</TableCell>
                  <TableCell align="center" width={110}>
                    Tovar
                  </TableCell>
                  <TableCell align="center" width={80}>
                    Tartib
                  </TableCell>
                  <TableCell align="right" width={150}>
                    Botda va ilovada
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 ? (
                  // Jadvalning o'zi qoladi: qidiruvda xato yozib to'g'rilansa
                  // 130 qator qaytadan chizilmasin.
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 5, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Topilmadi. Boshqa so'z bilan qidirib ko'ring.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  visible.map((c) => (
                    <CategoryRow
                      key={c.id}
                      c={c}
                      parentName={c.parentId ? byId.get(c.parentId)?.name || "" : ""}
                      saving={savingId === c.id}
                      open={expanded.has(c.id)}
                      onToggle={toggle}
                      onPatch={patch}
                      onPickParent={openPicker}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Ota papka tanlash — butun sahifada bitta oyna. */}
      <Dialog open={Boolean(picking)} onClose={() => setPickingId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Ota papka</DialogTitle>
        <DialogContent dividers>
          {picking && (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <b>{picking.name || "nomsiz"}</b> qaysi papka ichida tursin?
              </Typography>
              <Autocomplete
                openOnFocus
                // Tozalash (X) tugmasi yo'q: u bosilishi bilan kategoriya
                // ildizga ko'chib ketardi. Ildizga chiqarish -- pastdagi tugma.
                disableClearable
                size="small"
                value={picking.parentId ? byId.get(picking.parentId) || null : null}
                options={pickerOptions}
                getOptionLabel={(o) => o.name || "nomsiz"}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                // Kalit nom bo'yicha emas, id bo'yicha: 1C da bir xil nomli
                // papkalar uchraydi.
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box component="span" sx={{ pl: `${o.depth * 12}px` }}>
                      {o.name || "nomsiz"}
                    </Box>
                  </li>
                )}
                onChange={(_, v) => {
                  if (!v) return;
                  setPickingId(null);
                  move(picking.id, v.id);
                }}
                renderInput={(params) => (
                  <TextField {...params} autoFocus placeholder="— ildizda —" />
                )}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {picking?.parentId && (
            <Button
              color="inherit"
              onClick={() => {
                setPickingId(null);
                move(picking.id, null);
              }}
            >
              Ildizga chiqarish
            </Button>
          )}
          <Button onClick={() => setPickingId(null)}>Yopish</Button>
        </DialogActions>
      </Dialog>

      {/*
        Yuklashdan oldin nima o'zgarishi ko'rsatiladi. Ataylab: fayl butun
        katalogning tuzilishini almashtiradi va uni ortga qaytarish oson emas.
      */}
      <Dialog open={Boolean(preview)} onClose={closePreview} maxWidth="sm" fullWidth>
        <DialogTitle>Fayldan nima o'zgaradi</DialogTitle>
        <DialogContent dividers>
          {preview && (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Typography variant="body2">
                Faylda <b>{preview.folders}</b> ta papka topildi.
              </Typography>
              <Typography variant="body2">
                Yangi papka yaratiladi: <b>{(preview.created ?? []).length}</b>
                {(preview.created ?? []).length > 0 && (
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    {" — "}
                    {preview.created.slice(0, 6).join(", ")}
                    {preview.created.length > 6 && ` va yana ${preview.created.length - 6} ta`}
                  </Box>
                )}
              </Typography>
              <Typography variant="body2">
                Papkasi belgilanadi yoki almashadi: <b>{(preview.linked ?? []).length}</b> ta kategoriya
              </Typography>
              {(preview.stillRoot ?? []).length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Faylda uchramagan {preview.stillRoot.length} ta kategoriya
                  ildizda qoladi — ularni qo'lda joylashtirish kerak:{" "}
                  <b>{preview.stillRoot.join(", ")}</b>
                </Alert>
              )}
              {importError && <Alert severity="error">{importError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview} disabled={importing}>
            Bekor qilish
          </Button>
          <Button variant="contained" onClick={confirmImport} disabled={importing}>
            {importing ? "Yuklanmoqda..." : "Yuklash"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesPage;
